<script setup>
import {useAuthStore} from '~/store/auth'
import * as yup from 'yup'
import {Form, useForm} from 'vee-validate'
import {cloneDeep} from 'lodash'
import {objectHasChanges} from '~/utils/objectUtils'
import {useNotification} from '~/composables/useNotification'
import InputField from '~/components/forms/subelements/InputField.vue'
import Button from '~/components/common/Button.vue'
import CheckField from '~/components/forms/subelements/CheckField.vue'
import {useContactApi} from '~/composables/api/useContactApi'
import {omit} from 'lodash/object'

const requestDataFormRef = ref()
const loaded = ref(true)
const sending = ref(false)
const originalValue = ref()
const currentData = ref()
const notification = useNotification()
const contactApi = useContactApi()
const authStore = useAuthStore()

const currentUser = computed(() => {
  return authStore.user
})

const emptyRequestData = {
  referer: '',
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  terms: false
}

const requestDataSchema = yup.object({
  firstname: yup.string().required().label('Nombre'),
  lastname: yup.string().required().label('Apellidos'),
  email: yup.string().email().required().label('Email'),
  phone: yup.string().optional().label('Teléfono'),
  subject: yup.string().required().label('Asunto'),
  message: yup.string().required().label('Observaciones'),
  terms: yup
    .boolean()
    .required()
    .isTrue('Debes aceptar la política de privavidad')
    .label('Términos y Condiciones'),
})

const {validate, setValues} = useForm({
  validationSchema: requestDataSchema,
})

const hasChanges = computed(() => {
  return objectHasChanges(currentData.value, originalValue.value)
})

async function doValidation() {
  setValues(currentData.value)
  const result = await validate() // Valida todos los campos

  if (!result.valid) {
    requestDataFormRef.value.validate()
  }

  return {valid: result.valid, data: cloneDeep(currentData.value)}
}

async function onSubmit() {
  try {
    if (sending.value) return

    sending.value = true

    const formData = omit(currentData.value, ['terms'])

    await contactApi.send(formData)
    initFormData()

    notification.showSuccess(`La solicitud de información se ha enviado correctamente. Te responderemos en breve.`)
    sending.value = false
  } catch (error) {
    sending.value = false
    notification.showError(error)
  }
}

function initFormData() {
  const data = cloneDeep(emptyRequestData)
  data.referer = window.location.href
  data.firstname = currentUser.value?.firstname || ''
  data.lastname = currentUser.value?.lastname || ''
  data.email = currentUser.value?.email || ''
  data.phone = currentUser.value?.phone || ''

  originalValue.value = cloneDeep(data)
  currentData.value = cloneDeep(data)
}

onMounted(async () => {
  initFormData()
  loaded.value = true
})

defineExpose({
  doValidation
})
</script>

<template>
  <Form
    v-if="loaded && currentData"
    id="requestDataForm"
    ref="requestDataFormRef"
    class="grid grid-cols-12 lg:px-8 gap-4 py-4 !px-0"
    :validation-schema="requestDataSchema"
    @submit="onSubmit"
  >
    <InputField
      v-if="!authStore.isAuth"
      class="col-span-12"
      label="Nombre"
      inputId="firstname"
      name="firstname"
      v-model="currentData.firstname"
    />

    <InputField
      v-if="!authStore.isAuth"
      class="col-span-12"
      label="Apellidos"
      inputId="lastname"
      name="lastname"
      v-model="currentData.lastname"
    />

    <InputField
      v-if="!authStore.isAuth"
      class="col-span-12"
      type="email"
      label="Email"
      inputId="email"
      name="email"
      v-model="currentData.email"
    />

    <InputField
      v-if="!authStore.isAuth || authStore.isAuth && !currentUser.phone"
      class="col-span-12"
      type="tel"
      label="Teléfono"
      inputId="phone"
      name="phone"
      v-model="currentData.phone"
    />

    <InputField
      class="col-span-12"
      label="Asunto"
      inputId="subject"
      name="subject"
      v-model="currentData.subject"
    />

    <InputField
      class="col-span-12 text-center form-style"
      label="Mensaje"
      type="textarea"
      inputId="message"
      name="message"
      v-model="currentData.message"
    />

    <CheckField
      inputId="terms"
      name="terms"
      v-model="currentData.terms"
      class="col-span-12 mb-3 text-[0.9rem] lg:text-md"
    >
      He leído y acepto la
      <NuxtLink class="underline" href="/politica-de-privacidad" target="_blank">
        Política de Privacidad
      </NuxtLink>
    </CheckField>

    <div class="col-span-12 text-center mb-5 flex justify-end">
      <Button
        class="w-full"
        :disabled="!hasChanges || sending"
        :loading="sending"
        label="Enviar"
        type="submit"
      />
    </div>
  </Form>
</template>
